import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { SkeletonBlock } from "../Skeleton/Skeleton";

/**
 * Locked Holdings / Trade History on a League Play profile.
 *
 * Client League Play Profiles spec, item 21: when the league's portfolio
 * visibility is "Hidden during league", another member opening that trader's
 * League Profile still sees Posts, Performance and the League Record in full —
 * only these two tabs are withheld, and only until the league ends.
 *
 * The client asked for "a lock glyph from the icon library already in use" and
 * "skeleton rows". Two deliberate calls inside that:
 *
 *   1. The rows are the SAME geometry as PortfolioRowsSkeleton (44px asset
 *      chip, three stacked text lines, right-aligned value) so the tab keeps the
 *      shape of the thing being hidden rather than collapsing to a bare message.
 *
 *   2. They do NOT animate. Every other grey block in this product breathes,
 *      and that motion means "loading, wait". A pulsing block here would tell
 *      the viewer to wait for something that is never going to arrive. Static
 *      grey reads as "withheld", which is what this actually is. This is our
 *      call, not the client's wording — flag it if they want the breathe.
 */
const COPY = {
  holdings: "This trader's holdings will become visible when the league ends.",
  tradeHistory:
    "This trader's trade history will become visible when the league ends.",
};

const LockedRow = () => (
  <div className="px-4 md:px-5 py-3 border-b border-[#F0F2F5] last:border-b-0">
    <div className="flex items-start">
      <SkeletonBlock
        animate={false}
        rounded="rounded-full"
        width={44}
        height={44}
        className="shrink-0"
      />
      <div className="flex-1 ml-3 md:ml-4 min-w-0">
        <div className="flex items-start">
          <div className="flex-1 min-w-0 pr-3">
            <SkeletonBlock animate={false} height={16} width="58%" />
            <SkeletonBlock
              animate={false}
              height={12}
              width="34%"
              className="mt-2"
            />
            <SkeletonBlock
              animate={false}
              height={14}
              width="46%"
              className="mt-2.5"
            />
          </div>
          <SkeletonBlock
            animate={false}
            height={14}
            width={78}
            className="shrink-0"
          />
        </div>
      </div>
    </div>
  </div>
);

const LockedLeagueTab = ({ kind = "holdings", rows = 4 }) => (
  <div className="px-4 md:px-5 lg:px-8 pt-5 pb-8">
    <div className="flex items-start gap-3 mb-5">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280]">
        <FontAwesomeIcon icon={faLock} />
      </span>
      <p className="text-[14px] md:text-[15px] leading-relaxed text-[#4A4A4A] font-inter">
        {COPY[kind] || COPY.holdings}
      </p>
    </div>
    {/* aria-hidden: the rows carry no information, the sentence above does. */}
    <div aria-hidden="true" className="rounded-xl border border-[#F0F2F5]">
      {Array.from({ length: rows }).map((_, i) => (
        <LockedRow key={i} />
      ))}
    </div>
  </div>
);

export default LockedLeagueTab;
